import type { Pt } from "./pathcmds";

/** 2x3 affine [a, b, c, d, e, f]: x' = a·x + c·y + e, y' = b·x + d·y + f. Same layout as @canvas/schema Matrix. */
export type Affine = [number, number, number, number, number, number];

export const identity = (): Affine => [1, 0, 0, 1, 0, 0];
export const translate = (x: number, y: number): Affine => [1, 0, 0, 1, x, y];
export const scale = (sx: number, sy = sx): Affine => [sx, 0, 0, sy, 0, 0];
export const rotate = (rad: number): Affine => {
  const c = Math.cos(rad), s = Math.sin(rad);
  return [c, s, -s, c, 0, 0];
};

/** m1 · m2 — apply m2 first, then m1. */
export function multiply(m1: readonly number[], m2: readonly number[]): Affine {
  const [a1, b1, c1, d1, e1, f1] = m1;
  const [a2, b2, c2, d2, e2, f2] = m2;
  return [
    a1 * a2 + c1 * b2,
    b1 * a2 + d1 * b2,
    a1 * c2 + c1 * d2,
    b1 * c2 + d1 * d2,
    a1 * e2 + c1 * f2 + e1,
    b1 * e2 + d1 * f2 + f1,
  ];
}

/** Compose left to right: compose(A, B, C) applies A first. */
export function compose(...ms: readonly (readonly number[])[]): Affine {
  return ms.reduce<Affine>((acc, m) => multiply(m, acc), identity());
}

export function invert(m: readonly number[]): Affine | null {
  const [a, b, c, d, e, f] = m;
  const det = a * d - b * c;
  if (Math.abs(det) < 1e-12) return null;
  const id = 1 / det;
  return [d * id, -b * id, -c * id, a * id, (c * f - d * e) * id, (b * e - a * f) * id];
}

export function apply(m: readonly number[], x: number, y: number): Pt {
  return { x: m[0] * x + m[2] * y + m[4], y: m[1] * x + m[3] * y + m[5] };
}

/** Apply only the linear part (for vectors/deltas). */
export function applyVector(m: readonly number[], x: number, y: number): Pt {
  return { x: m[0] * x + m[2] * y, y: m[1] * x + m[3] * y };
}

export interface Decomposed {
  x: number;
  y: number;
  /** Radians. */
  rotation: number;
  scaleX: number;
  scaleY: number;
  /** Shear factor (tan of skew angle) along x after rotation. */
  skew: number;
}

/** Decompose as T · R · Skew · S (QR-style). Exact for any non-degenerate affine. */
export function decompose(m: readonly number[]): Decomposed {
  const [a, b, c, d, e, f] = m;
  const scaleX = Math.hypot(a, b);
  const rotation = Math.atan2(b, a);
  const det = a * d - b * c;
  const scaleY = scaleX ? det / scaleX : Math.hypot(c, d);
  const skew = scaleX && scaleY ? (a * c + b * d) / (scaleX * scaleX) * (scaleX / scaleY) : 0;
  return { x: e, y: f, rotation, scaleX, scaleY, skew };
}

export function recompose(p: Decomposed): Affine {
  return compose([p.scaleX, 0, 0, p.scaleY, 0, 0], [1, 0, p.skew, 1, 0, 0], rotate(p.rotation), translate(p.x, p.y));
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function boundsOfPoints(pts: Pt[]): Rect {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const p of pts) {
    x0 = Math.min(x0, p.x);
    y0 = Math.min(y0, p.y);
    x1 = Math.max(x1, p.x);
    y1 = Math.max(y1, p.y);
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** Axis-aligned bounds of rect r after transform m. */
export function transformRect(m: readonly number[], r: Rect): Rect {
  return boundsOfPoints([apply(m, r.x, r.y), apply(m, r.x + r.w, r.y), apply(m, r.x + r.w, r.y + r.h), apply(m, r.x, r.y + r.h)]);
}

export function unionRects(rs: Rect[]): Rect | null {
  if (!rs.length) return null;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const r of rs) {
    x0 = Math.min(x0, r.x);
    y0 = Math.min(y0, r.y);
    x1 = Math.max(x1, r.x + r.w);
    y1 = Math.max(y1, r.y + r.h);
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

export function rectsIntersect(a: Rect, b: Rect): boolean {
  return a.x <= b.x + b.w && b.x <= a.x + a.w && a.y <= b.y + b.h && b.y <= a.y + a.h;
}

export function rectContainsRect(outer: Rect, inner: Rect): boolean {
  return inner.x >= outer.x && inner.y >= outer.y && inner.x + inner.w <= outer.x + outer.w && inner.y + inner.h <= outer.y + outer.h;
}

export function approxEqual(m1: readonly number[], m2: readonly number[], eps = 1e-9): boolean {
  return m1.every((v, i) => Math.abs(v - m2[i]) < eps);
}
